import { useState, useMemo, useEffect, useCallback } from 'react'
import { heapEvent } from '@equipmentshare/heaper'

import {
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
	EuiLoadingSpinner,
	EuiOverlayMask,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiPanel,
} from '@equipmentshare/ds2'

import RefreshButton from '../../components/RefreshButton'
import SearchBar from '../../components/SearchBar'
import StyledHeader from '../../components/StyledHeader'
import PermissionsCheck from 'components/PermissionsCheck'
import PageTitle from 'views/components/PageTitle/PageTitle'
import { useDispatchSetSelectedModal } from 'contexts/modal-context/modal-context'
import { TABLE_HEIGHT } from 'views/helpers/utils'

import BranchList from './components/BranchList'

import { Permission } from '../../../graphql'
import useInventoryLocationsSearch, { BranchWithInventoryLocations, groupInventoryLocationsByBranch, sortByName } from 'hooks/inventory-location-search'
import { getAddInventoryLocationModal } from './components/modals'
import { NetworkStatus } from '@apollo/client'
import { InventoryLocation } from 'inventory-web'


export default function InventoryLocations() {
	const [ branches, setBranches ] = useState<BranchWithInventoryLocations[]>( [] )
	const [ search, setSearch ] = useState( '' )
	const [ expandedIds, setExpandedIds ] = useState<number[]>( [] )
	const [ isDeletingStore, setIsDeletingStore ] = useState( false )

	const { filteredInventoryLocations, loading, refetch, networkStatus } = useInventoryLocationsSearch( search, undefined, undefined, undefined, undefined, true )

	useEffect( () => {
		const newBranches = groupInventoryLocationsByBranch( filteredInventoryLocations )
		setBranches( newBranches )
		if ( search !== '' ) {
			setExpandedIds( newBranches.map( ( branch ) => branch.id ) )
		} else {
			setExpandedIds( [] )
		}
	}, [ filteredInventoryLocations, search ] )

	const updateInventoryLocationAfterEdit = useCallback( ( updatedInventoryLocation: InventoryLocation, clearOtherDefaults?: boolean ) => {
		setBranches( ( prev ) => prev.map( ( branch ) => {
			if ( branch.id === updatedInventoryLocation.branch.id ) {
				return {
					...branch,
					inventoryLocations: branch.inventoryLocations.map( ( inventoryLocation ) => {
						if ( inventoryLocation.id === updatedInventoryLocation.id ) {
							return {
								...updatedInventoryLocation,
								label: updatedInventoryLocation.name,
							}
						} else if ( clearOtherDefaults ) {
							return {
								...inventoryLocation,
								defaultLocation: false,
							}
						}
						return inventoryLocation
					} )
				}
			}
			return branch
		} ) )
	}, [] )

	const updateListAfterDelete = useCallback( ( removedId: string, branchId: number ) => {
		setBranches( ( prev ) => prev.map( ( branch ) => {
			if ( branch.id === branchId ) {
				return {
					...branch,
					inventoryLocations: branch.inventoryLocations.filter( ( inventoryLocation ) => inventoryLocation.id !== removedId )
				}
			}
			return branch
		} ) )
	}, [] )

	const updateListAfterMove = useCallback( ( oldInventoryLocationId: string, movedInventoryLocation: InventoryLocation, fromBranchId: number, toBranchId: number ) => {
		setBranches( ( prev ) => prev.map( ( branch ) => {
			if ( branch.id === fromBranchId ) {
				return {
					...branch,
					inventoryLocations: branch.inventoryLocations.filter( ( inventoryLocation ) => inventoryLocation.id !== oldInventoryLocationId )
				}
			}

			if ( branch.id === toBranchId ) {
				return {
					...branch,
					inventoryLocations: [
						...branch.inventoryLocations,
						{
							...movedInventoryLocation,
							label: movedInventoryLocation.name,
						},
					].sort( sortByName )
				}
			}

			return branch
		} ) )
	}, [] )

	const onToggleExpandedById = useCallback( ( id: number ) => {
		setExpandedIds( ( prev ) => ( prev.includes( id ) ? prev.filter( ( expandedId ) => expandedId !== id ) : [ ...prev, id ] ) )
	}, [] )

	const isLoading = useMemo( () => ( loading || networkStatus === NetworkStatus.refetch ), [ loading, networkStatus ] )

	const setSelectedModal = useDispatchSetSelectedModal()
	const fullHeight = 'eui-fullHeight'

	const onAddStore = () => {
		heapEvent( 'Inventory - Stores - Click - Add Store', {} )
		setSelectedModal( getAddInventoryLocationModal( refetch ) )
	}

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody className={fullHeight}>
				{ isDeletingStore && (
					<EuiOverlayMask>
						<EuiLoadingSpinner size="xl" />
					</EuiOverlayMask>
				) }
				<PageTitle title="Stores" />
				<StyledHeader>
					<EuiFlexGroup wrap>
						<EuiFlexItem style={{ minWidth: 677 }}>
							<div className="content searchFilter">
								<SearchBar search={search} setSearch={setSearch} />
								<RefreshButton refetchFunction={refetch} />
							</div>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<div className="content actions">
								<PermissionsCheck permissionsToCheck={[ Permission.CanCreateTopLevelStore ]}>
									<EuiButton
										fill
										onClick={onAddStore}
									>
										Add store
									</EuiButton>
								</PermissionsCheck>
							</div>
						</EuiFlexItem>
					</EuiFlexGroup>
				</StyledHeader>
				<EuiPageSection className={fullHeight} paddingSize="none">
					<EuiPanel paddingSize="none" className="eui-yScroll" hasShadow={false} style={{ height: TABLE_HEIGHT }}>
						<BranchList
							branches={branches}
							updateAfterEdit={updateInventoryLocationAfterEdit}
							updateAfterDelete={updateListAfterDelete}
							updateAfterMove={updateListAfterMove}
							isLoading={isLoading}
							expandedIds={expandedIds}
							onToggleExpandedById={onToggleExpandedById}
							setIsDeletingStore={setIsDeletingStore}
						/>
					</EuiPanel>
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
